import { useEffect, useState } from "react";
import { Link } from "wouter";
import type { User } from "../lib/types";
import { LECTURES } from "../data/lectures";
import { Sidebar } from "../components/Sidebar";
import { TopBar } from "../components/TopBar";
import { I } from "../components/Icons";

interface Props {
  user: User;
  onSignOut: () => void;
}

export function ManageLectures({ user, onSignOut }: Props) {
  const [lockedIds, setLockedIds] = useState<Set<string> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  async function load() {
    try {
      const res = await fetch("/api/lectures/locks", { credentials: "include" });
      if (!res.ok) {
        setError(
          res.status === 403
            ? "You don't have access to this page."
            : `Failed to load (${res.status}).`,
        );
        return;
      }
      const data = (await res.json()) as { locked: string[] };
      setLockedIds(new Set(data.locked));
      setError(null);
    } catch {
      setError("Couldn't reach the server.");
    }
  }

  useEffect(() => {
    void load();
  }, []);

  async function toggle(lectureId: string, locked: boolean) {
    setBusyId(lectureId);
    try {
      const res = await fetch("/api/admin/lectures/lock", {
        method: "POST",
        credentials: "include",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ lectureId, locked }),
      });
      const data = (await res.json().catch(() => ({}))) as {
        locked?: string[];
        error?: string;
      };
      if (!res.ok) {
        setError(data.error ?? `Failed to update (${res.status}).`);
        return;
      }
      setLockedIds(new Set(data.locked ?? []));
      setError(null);
    } catch {
      setError("Couldn't reach the server.");
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="gf-shell">
      <Sidebar active="manage-lectures" user={user} onSignOut={onSignOut} />
      <div className="gf-main">
        <TopBar
          crumbs={[
            { t: "Git Fit", href: "/dashboard" },
            { t: "Team progress", href: "/admin" },
            { t: "Manage lectures", strong: true },
          ]}
        />
        <div className="gf-dash">
          <div className="gf-dash__section-head">
            <div>
              <h2>Manage lectures</h2>
              <p>
                Lock a lecture to gray it out on every learner's dashboard and
                stop them opening it. You can still preview and present a locked
                lecture as an admin. Unlock to make it available again. Changes
                take effect the next time a learner loads their dashboard.
              </p>
            </div>
            <Link href="/admin" className="gf-btn gf-btn--secondary">
              <I.Eye size={15} /> Team progress
            </Link>
          </div>

          {error && (
            <div
              className="gf-card"
              style={{
                padding: "var(--space-5)",
                color: "var(--danger)",
                marginBottom: "var(--space-5)",
              }}
            >
              {error}
            </div>
          )}

          {!lockedIds ? (
            <div
              className="gf-card"
              style={{ padding: "var(--space-5)", color: "var(--fg-3)" }}
            >
              Loading…
            </div>
          ) : (
            <LectureLockTable
              lockedIds={lockedIds}
              busyId={busyId}
              onToggle={toggle}
            />
          )}
        </div>
      </div>
    </div>
  );
}

function LectureLockTable({
  lockedIds,
  busyId,
  onToggle,
}: {
  lockedIds: Set<string>;
  busyId: string | null;
  onToggle: (lectureId: string, locked: boolean) => void;
}) {
  const th: React.CSSProperties = {
    textAlign: "left",
    padding: "10px 12px",
    fontSize: "var(--fs-caption)",
    color: "var(--fg-3)",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "var(--ls-wide)",
    borderBottom: "1px solid var(--border-2)",
    whiteSpace: "nowrap",
  };
  const td: React.CSSProperties = {
    padding: "12px",
    borderBottom: "1px solid var(--border-1)",
    fontSize: "var(--fs-body-sm)",
    verticalAlign: "middle",
  };

  return (
    <div className="gf-card" style={{ padding: 0, overflowX: "auto" }}>
      <table style={{ borderCollapse: "collapse", width: "100%", minWidth: 640 }}>
        <thead>
          <tr>
            <th style={th}>Lecture</th>
            <th style={{ ...th, textAlign: "center" }}>Status</th>
            <th style={{ ...th, textAlign: "right" }}>Action</th>
          </tr>
        </thead>
        <tbody>
          {LECTURES.map((l) => {
            const locked = lockedIds.has(l.id);
            const busy = busyId === l.id;
            return (
              <tr key={l.id}>
                <td style={td}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <span
                      style={{
                        fontSize: "var(--fs-caption)",
                        fontWeight: 700,
                        color: "var(--fg-link)",
                        letterSpacing: "0.08em",
                        textTransform: "uppercase",
                      }}
                    >
                      {l.eyebrow}
                    </span>
                    <div>
                      <div style={{ fontWeight: 600, color: "var(--fg-1)" }}>
                        {l.title}
                      </div>
                      <div
                        style={{
                          fontSize: "var(--fs-caption)",
                          color: "var(--fg-3)",
                        }}
                      >
                        {l.duration} · {l.coversLabel}
                      </div>
                    </div>
                  </div>
                </td>
                <td style={{ ...td, textAlign: "center" }}>
                  <LockChip locked={locked} />
                </td>
                <td style={{ ...td, textAlign: "right" }}>
                  <button
                    className={`gf-btn ${locked ? "gf-btn--primary" : "gf-btn--secondary"}`}
                    disabled={busy}
                    onClick={() => onToggle(l.id, !locked)}
                  >
                    {locked ? (
                      <>
                        <I.Unlock size={14} /> {busy ? "Unlocking…" : "Unlock"}
                      </>
                    ) : (
                      <>
                        <I.Lock size={14} /> {busy ? "Locking…" : "Lock"}
                      </>
                    )}
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function LockChip({ locked }: { locked: boolean }) {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        padding: "2px 10px",
        borderRadius: 999,
        fontSize: "var(--fs-caption)",
        fontWeight: 600,
        background: locked ? "rgba(0,24,71,0.08)" : "rgba(16,185,129,0.12)",
        color: locked ? "var(--fg-2)" : "var(--success)",
        border: "1px solid var(--border-1)",
      }}
    >
      {locked ? <I.Lock size={11} /> : <I.Unlock size={11} />}
      {locked ? "Locked" : "Available"}
    </span>
  );
}
